import React from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Select,
} from 'antd';
import moment from 'moment';
import {
  NotePencil,
  User,
} from '@phosphor-icons/react';
import DatePicker from '../../../components/DatePicker';
import Typography from '../../../components/Typography';
import Button from '../../../components/Button';
import COLORS from '../../../util/constants/colors';
import { NOTE_MAX_LENGTH } from './callFollowUpFormUtils';

export const formatEntryDate = (createdAt) => (
  createdAt ? moment(createdAt).format('MMM D, YYYY h:mm A') : ''
);

export const formatEntryCreator = (entry) => (
  entry.created_by?.name || entry.created_by?.email || 'Unknown user'
);

export const followUpOwnerDisplayName = (followup) => (
  followup.assignee?.name || followup.assignee?.email || 'Unassigned'
);

export const FollowUpField = ({
  className,
  labelClassName,
  icon,
  label,
  value,
}) => (
  <div className={className}>
    <div className={labelClassName}>
      {icon}
      <Typography variant="text" size="xsmall" color={COLORS.TEXT_SECONDARY_LITE}>{label}</Typography>
    </div>
    <Typography variant="text" size="small">{value || 'N/A'}</Typography>
  </div>
);

export const FollowUpOwnerField = ({
  followup,
  className,
  labelClassName,
}) => (
  <FollowUpField
    className={className}
    labelClassName={labelClassName}
    icon={<User size={16} />}
    label="Assignee"
    value={followUpOwnerDisplayName(followup)}
  />
);

export const FollowUpFormFields = ({
  form,
  formGridClassName,
  inputClassName,
  assigneeOptions,
  dispositionOptions,
  controlsDisabled,
  updateField,
}) => (
  <div className={formGridClassName}>
    <div className={inputClassName}>
      <span>Assignee</span>
      <Select
        aria-label="Select assignee"
        value={form.assigneeId}
        onChange={(value) => updateField('assigneeId', value || null)}
        options={assigneeOptions}
        placeholder="Select assignee"
        allowClear
        disabled={controlsDisabled}
      />
    </div>
    <div className={inputClassName}>
      <span>Due date</span>
      <DatePicker
        aria-label="Due date"
        value={form.dueDate ? moment(form.dueDate, 'YYYY-MM-DD') : null}
        onChange={(_, dateString) => updateField('dueDate', dateString || null)}
        disabled={controlsDisabled}
      />
    </div>
    <div className={inputClassName}>
      <span>Final outcome</span>
      <Select
        aria-label="Select outcome"
        value={form.callDispositionId}
        onChange={(value) => updateField('callDispositionId', value || null)}
        options={dispositionOptions}
        placeholder="Select outcome"
        allowClear
        disabled={controlsDisabled}
      />
    </div>
  </div>
);

export const AddEntryForm = ({
  addEntryClassName,
  addStatusGridClassName,
  actionsClassName,
  inputClassName,
  noteInputClassName,
  entryForm,
  setEntryForm,
  statusOptions,
  controlsDisabled,
  saving,
  showCancel,
  submitLabel,
  submitSize,
  onCancel,
  onSubmit,
}) => {
  const statusControl = (
    <div className={inputClassName}>
      <span>New status</span>
      <Select
        aria-label="New entry status"
        value={entryForm.statusId}
        onChange={(value) => setEntryForm({ ...entryForm, statusId: value })}
        options={statusOptions}
        disabled={controlsDisabled}
      />
    </div>
  );

  return (
    <div className={addEntryClassName}>
      {addStatusGridClassName ? <div className={addStatusGridClassName}>{statusControl}</div> : statusControl}
      <div className={noteInputClassName}>
        <span>Note</span>
        <Input.TextArea
          aria-label="Note"
          value={entryForm.note}
          onChange={(event) => setEntryForm({ ...entryForm, note: event.target.value })}
          placeholder="Optional note"
          maxLength={NOTE_MAX_LENGTH}
          disabled={controlsDisabled}
        />
      </div>
      <div className={actionsClassName}>
        {showCancel && (
          <Button
            type="secondary"
            size="small"
            label="Cancel"
            onClick={onCancel}
            disabled={saving}
          />
        )}
        <Button
          type="primary"
          size={submitSize}
          label={submitLabel}
          leftIcon={<NotePencil size={16} />}
          onClick={onSubmit}
          disabled={controlsDisabled}
        />
      </div>
    </div>
  );
};

FollowUpField.propTypes = {
  className: PropTypes.string.isRequired,
  labelClassName: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

FollowUpField.defaultProps = {
  value: null,
};

FollowUpOwnerField.propTypes = {
  followup: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
  labelClassName: PropTypes.string.isRequired,
};

FollowUpFormFields.propTypes = {
  form: PropTypes.object.isRequired,
  formGridClassName: PropTypes.string.isRequired,
  inputClassName: PropTypes.string.isRequired,
  assigneeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  dispositionOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  controlsDisabled: PropTypes.bool.isRequired,
  updateField: PropTypes.func.isRequired,
};

AddEntryForm.propTypes = {
  addEntryClassName: PropTypes.string.isRequired,
  addStatusGridClassName: PropTypes.string,
  actionsClassName: PropTypes.string.isRequired,
  inputClassName: PropTypes.string.isRequired,
  noteInputClassName: PropTypes.string.isRequired,
  entryForm: PropTypes.object.isRequired,
  setEntryForm: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  controlsDisabled: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  showCancel: PropTypes.bool.isRequired,
  submitLabel: PropTypes.string.isRequired,
  submitSize: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
};

AddEntryForm.defaultProps = {
  addStatusGridClassName: null,
  onCancel: () => {},
};
